import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import {
  downloadCanvasAsPNG,
  drawConsolidation,
  drawFacings,
  drawLabelReadings,
  drawLabels,
  IMAGE_DOWNLOAD_TYPES,
  LABEL_DISPLAY,
  metersToPixels,
  READING_AI_TYPES,
} from '../tools/Canvas/utils';

const getCanvasElements = (canvasId, containerId) => {
  const canvasContainer = document.getElementById(containerId);
  const canvas = document.getElementById(canvasId);

  if (!canvasContainer || !canvas) {
    return null;
  }

  return { canvasContainer, canvas };
};

const Canvas = props => {
  const factor = props.factor || (props.onModal ? 2 : 1);
  const defaultZoom = props.defaultZoom ? props.defaultZoom : 50;

  const [zoomValuevh, setZoomValuevh] = useState(defaultZoom);
  const [open, setOpen] = useState(false);
  const [DetectionId, setDetectionId] = useState(null);
  const [diffX, setDiffX] = useState(0);
  const [isFailureLoadingImg, setIsFailureLoadingImg] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const currentImgRef = useRef(null);

  const theme = useTheme();
  const { t } = useTranslation();

  const IMAGECANVAS = props.imageCanvas
    ? props.imageCanvas
    : props.onModal
      ? 'modalImageCanvas'
      : 'imageCanvas';
  const CANVASCONTAINER = props.canvasContainer
    ? props.canvasContainer
    : props.onModal
      ? 'modalCanvasContainer'
      : 'canvasContainer';

  useEffect(() => {
    const elements = getCanvasElements(IMAGECANVAS, CANVASCONTAINER);

    if (!elements) {
      return;
    }

    const { canvasContainer, canvas } = elements;
    canvasContainer.style.cursor = props.onModal ? 'arrow' : 'all-scroll';
    canvas.style.cursor = props.onModal ? 'arrow' : 'grab';
    if (!props.onModal && props.image_url) {
      canvasContainer.addEventListener('wheel', handleWheel);
      canvasContainer.addEventListener('mousedown', handleMouseDown);
      canvasContainer.addEventListener('mouseup', handleMouseUp);
      canvasContainer.addEventListener('mousemove', handleMouseMove);
      canvasContainer.addEventListener('contextmenu', handlecontextmenu);
      canvasContainer.addEventListener('pointermove', handlePointerMove);
      canvasContainer.addEventListener('click', handleClick);
    }

    return () => {
      if (!props.onModal) {
        canvasContainer.removeEventListener('wheel', handleWheel);
        canvasContainer.removeEventListener('mousedown', handleMouseDown);
        canvasContainer.removeEventListener('mouseup', handleMouseUp);
        canvasContainer.removeEventListener('mousemove', handleMouseMove);
        canvasContainer.removeEventListener('contextmenu', handlecontextmenu);
        canvasContainer.removeEventListener('pointermove', handlePointerMove);
        canvasContainer.removeEventListener('click', handleClick);
      }
    };
  }, [
    zoomValuevh,
    props.isLoadingImages,
    props.isLoadingSecondaryImages,
    defaultZoom,
    props.onModal,
    props.image_url,
  ]);

  useEffect(() => {
    setDiffX(0);
    setZoomValuevh(defaultZoom);
  }, [props.defaultZoom]);

  useEffect(() => {
    if (!props.onModal && !props.image_url) {
      setIsFailureLoadingImg(false);
      setOpen(false);
      setDetectionId(null);
      setDiffX(0);
      setZoomValuevh(defaultZoom);
      clean();
      cleanModal();
    }
  }, [props.image_url, props.ai, props.isLoadingImages, props.metadata, props.isLoadingImageUrl, props.onModal, defaultZoom]);

  useEffect(() => {
    if (props.image_url && props.onModal) {
      setDiffX(0);
      cleanModal();
    }
  }, [props.isLoadingSecondaryImages]);

  useEffect(() => {
    setDiffX(0);

    if (!props.image_url) {
      currentImgRef.current = null;
      clean();
      return;
    }

    setIsFailureLoadingImg(false);
    drawImage();

    return () => {
      currentImgRef.current = null;
    };
  }, [
    props.image_url,
    props.ai,
    props.metadata,
    props.formatCurrency,
    props.canShowSecondStage,
    props.defaultZoom,
    props.splitView,
    props.stage,
    props.showVertexNumbers,
    props.predictionColors,
  ]);

  useEffect(() => {
    if (props.image_url) {
      drawImage();
    }
  }, [props.opacity]);

  const clean = () => {
    const elements = getCanvasElements(IMAGECANVAS, CANVASCONTAINER);

    if (!elements) {
      return;
    }

    const { canvas } = elements;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    ctx.canvas.width = ctx.canvas.width; // ? Yes, this is correct. The canvas clears when there is a change on its dimensions even if the dimensions are the same.
    //TODO: In the future we should change it to ctx.reset() when it's supported by all browsers (https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/reset)
  };

  const cleanModal = () => {
    if (props.onModal) {
      const elements = getCanvasElements(IMAGECANVAS, CANVASCONTAINER);

      if (!elements) {
        return;
      }

      const { canvas } = elements;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const drawImage = () => {
    const elements = getCanvasElements(IMAGECANVAS, CANVASCONTAINER);

    if (!elements) {
      return;
    }

    const { canvasContainer: container, canvas } = elements;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const img = new Image();

    currentImgRef.current = img;

    if (props.image_url) {
      img.src = props.image_url?.image_url;
    }

    // handle image loading error
    img.onerror = function () {
      if (currentImgRef.current !== img) return;
      setIsFailureLoadingImg(true);
    };

    img.onload = function () {
      if (currentImgRef.current !== img) return;
      canvas.width = img.naturalWidth * factor;
      canvas.height = img.naturalHeight * factor;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      if (props.ai !== 'Shelfout Detection' && props.ai !== 'Consolidation' && canvas.scrollWidth > container.offsetWidth) {
        setDiffX(
          defaultZoom -
          (container.offsetWidth / canvas.scrollWidth) * defaultZoom,
        );
      }

      if (props.readingCanvas) {
        drawReadingShade(canvas);
      }

      if (!props.isLoadingImages && !props.isLoadingSecondaryImages && !props.isLoadingImageUrl) {
        drawObjects(canvas);
      }
    };
  };

  const drawReadingShade = canvas => {
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = `rgba(0,0,0,${props.opacity})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // Draws what this canvas shows on top of the image: the reading shade, if any, and the objects.
  const drawOverlay = canvas => {
    if (props.readingCanvas) {
      drawReadingShade(canvas);
    }
    drawObjects(canvas);
  };

  // Draws the objects of the current AI on the target canvas element.
  const drawObjects = target => {
    if (props.ai === 'Consolidation' && props.metadata && Object.keys(props.metadata).length > 0) {
      drawConsolidation(
        props.metadata,
        target,
        theme,
        factor,
      );
    } else if (props.metadata && props.metadata[0] && props.metadata[0].x1 !== undefined) {
      drawLabels(
        props.metadata,
        target,
        theme,
        factor,
        props.selectedLabel,
        props.showVertexNumbers,
      );
    } else if (props.metadata && (props.ai === 'Shelfout Detection' || props.metadata.some(object => object.prediction_bbox))) {
      drawFacings(
        props.metadata,
        target,
        theme,
        factor,
        props.predictionColors,
        props.showVertexNumbers,
      );
    } else if (props.metadata && props.metadata[0]) {
      drawLabelReadings(
        props.metadata,
        props.currencyFormat,
        props.formatCurrency,
        target,
        theme,
        props.onModal ? true : false,
        false,
        factor,
        props.readingCanvas,
        props.chain,
        props.splitView,
        props.stage,
        props.predictionColors,
        props.showVertexNumbers,
      );
    }
  };

  const handleDownload = async imageType => {
    setDownloadMenuAnchor(null);
    const { store, session, blobPath, imageId } = props.downloadContext;
    const response = await props.downloadImage({
      store,
      session,
      blobPath,
      aiType: props.ai,
      imageId,
      imageType,
    });
    if (!response) return;

    const withObjects = imageType === IMAGE_DOWNLOAD_TYPES.WITH_OBJECTS;
    const fileNameParts = [store, session, imageId, imageType, withObjects && props.readingCanvas && 'readings'];
    downloadCanvasAsPNG({
      fileName: `${fileNameParts.filter(Boolean).join('_')}.png`,
      imageSrc: `data:${response.content_type};base64,${response.image}`,
      factor: withObjects ? factor : 1,
      drawOverlay: withObjects ? drawOverlay : undefined,
    }).catch(error => console.error('error: ', error));
  };

  const handleWheel = event => {
    event.preventDefault();
    const container = document.getElementById(CANVASCONTAINER);
    const { scrollLeft: oldScrollLeft, scrollTop: oldscrollTop } = container;
    const { offsetX, offsetY, deltaY } = event;

    const minZoom = defaultZoom;
    const maxZoom = 2000;
    let newZoom = zoomValuevh * (deltaY * -0.001 + 1);
    newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));

    const ratio = newZoom / zoomValuevh;
    setZoomValuevh(newZoom);

    container.scrollLeft = oldScrollLeft + offsetX * (ratio - 1);
    container.scrollTop = oldscrollTop + offsetY * (ratio - 1);
  };

  const handleMouseDown = event => {
    const container = document.getElementById(CANVASCONTAINER);
    container.style.cursor = 'grabbing';
    const canvas = document.getElementById(IMAGECANVAS);
    canvas.style.cursor = 'grabbing';
    container.dataset.scrollLeft = container.scrollLeft;
    container.dataset.scrollTop = container.scrollTop;
    container.dataset.clientX = event.clientX;
    container.dataset.clientY = event.clientY;
  };

  const handleMouseUp = event => {
    const container = document.getElementById(CANVASCONTAINER);
    const canvas = document.getElementById(IMAGECANVAS);
    container.style.cursor = 'all-scroll';
    canvas.style.cursor = 'grab';
  };

  const handleMouseMove = event => {
    const container = document.getElementById(CANVASCONTAINER);
    if (container.style.cursor === 'grabbing') {
      container.scrollLeft =
        container.dataset.scrollLeft -
        (event.clientX - container.dataset.clientX);
      container.scrollTop =
        container.dataset.scrollTop -
        (event.clientY - container.dataset.clientY);
    }
  };

  const getMatchingLabel = (xImage, yImage) => {
    if (!Array.isArray(props.metadata)) {
      return null;
    }

    return props.metadata.find(label => {
      const isInsideLabelBounds =
        label.x1 !== undefined &&
        label.x2 !== undefined &&
        label.y1 !== undefined &&
        label.y2 !== undefined &&
        xImage > label.x1 * factor &&
        xImage < label.x2 * factor &&
        yImage > label.y1 * factor &&
        yImage < label.y2 * factor;

      // Label Reading stores the bbox as a rotated quadrilateral in bbox_coords.
      // text_recognition/text_recognition_postprocessed store it as a list of
      // quadrilaterals (one per detected text). Hit-test each polygon individually.
      const isInsidePolygon = (polygon) => {
        if (!Array.isArray(polygon) || polygon.length < 3) return false;
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i][0] * factor;
          const yi = polygon[i][1] * factor;
          const xj = polygon[j][0] * factor;
          const yj = polygon[j][1] * factor;
          const intersects =
            yi > yImage !== yj > yImage &&
            xImage < ((xj - xi) * (yImage - yi)) / (yj - yi) + xi;
          if (intersects) inside = !inside;
        }
        return inside;
      };
      let isInsideBboxCoords = false;
      if (Array.isArray(label.bbox_coords) && label.bbox_coords.length > 0) {
        const isNestedPolygons = Array.isArray(label.bbox_coords[0]?.[0]);
        if (isNestedPolygons) {
          isInsideBboxCoords = label.bbox_coords.some(polygon => isInsidePolygon(polygon));
        } else {
          isInsideBboxCoords = isInsidePolygon(label.bbox_coords);
        }
      }

      const isInsidePredictionBounds =
        props.ai === 'Shelfout Detection' &&
        label.prediction_bbox &&
        xImage > label.prediction_bbox[0] * factor &&
        xImage < label.prediction_bbox[2] * factor &&
        yImage > label.prediction_bbox[1] * factor &&
        yImage < label.prediction_bbox[3] * factor;

      return isInsideLabelBounds || isInsideBboxCoords || isInsidePredictionBounds;
    });
  };

  const handlecontextmenu = event => {
    event.preventDefault();
    const canvas = document.getElementById(IMAGECANVAS);
    const x = event.offsetX;
    const y = event.offsetY;
    const xImage = x * (canvas.width / canvas.clientWidth);
    const yImage = y * (canvas.height / canvas.clientHeight);
    const label = getMatchingLabel(xImage, yImage);

    if (label) {
      const selectedLabel =
        props.ai === 'Product Detection'
          ? label.product_detection_id
          : label.detection_id;

      props.setSelectedLabel(selectedLabel);
      setOpen(false);

      if (READING_AI_TYPES.includes(props.ai)) {
        const capture_id = label.capture_id ?? selectedLabel.split('_')[0];
        props.handleOpen(capture_id, 'capture', props.metadata);
      } else if (props.ai === 'Product Detection') {
        props.handleOpen(
          {
            product_detection_id: label.product_detection_id,
            capture_id: label.capture_id,
          },
          'product_detection_id',
          props.metadata,
        );
      } else {
        props.handleOpen(selectedLabel, 'detection', props.metadata);
      }
    }
  };

  const handleClick = event => {
    const container = document.getElementById(CANVASCONTAINER);
    const movedX = Math.abs(event.clientX - parseFloat(container.dataset.clientX));
    const movedY = Math.abs(event.clientY - parseFloat(container.dataset.clientY));

    if (movedX > 5 || movedY > 5) {
      return;
    }

    if (props.ai === 'Consolidation' && props.metadata) {
      const canvas = document.getElementById(IMAGECANVAS);
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const xImage = x * (canvas.width / canvas.clientWidth);
      const yImage = y * (canvas.height / canvas.clientHeight);

      const clickedItems = [];

      Object.keys(props.metadata).forEach(stageName => {
        const stageItems = props.metadata[stageName];
        const stageInfo = LABEL_DISPLAY[stageName];
        if (stageInfo) {
          const [sizeX, sizeY] = stageInfo.size;
          stageItems.forEach(item => {
            const [pixelX, pixelY] = metersToPixels([item.x, item.y]);
            const [x1, y1] = [pixelX - sizeX / 2, pixelY - sizeY / 2];
            const [x2, y2] = [pixelX + sizeX / 2, pixelY + sizeY / 2];

            if (xImage >= x1 && xImage <= x2 && yImage >= y1 && yImage <= y2) {
              clickedItems.push({ ...item, order: stageInfo.order, stageName, type: item.type });
            }
          });
        }
      });
      if (clickedItems.length > 0) {
        clickedItems.sort((a, b) => b.order - a.order);
        props.handleConsolidationClick(clickedItems[0]);
      }
    }
  };

  const handlePointerMove = event => {
    const canvas = document.getElementById(IMAGECANVAS);
    const x = event.offsetX;
    const y = event.offsetY;
    const xImage = x * (canvas.width / canvas.clientWidth);
    const yImage = y * (canvas.height / canvas.clientHeight);
    if (canvas.style.cursor !== 'grabbing') {
      canvas.style.cursor = 'grab';
      setOpen(false);
      const label = getMatchingLabel(xImage, yImage);

      if (label) {
        setOpen(
          !READING_AI_TYPES.includes(props.ai),
        );
        setDetectionId(
          props.ai === 'Product Detection'
            ? label.detection_id
              ? label.product_detection_id
              : `${label.product_detection_id} ⚠️`
            : !READING_AI_TYPES.includes(props.ai)
              ? label.detection_id
              : null,
        );
        canvas.style.cursor = 'context-menu';
      } else {
        setOpen(false);
        setDetectionId(null);
      }
    }
  };

  return (
    <>
      {isFailureLoadingImg ? (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'grey',
            fontSize: '1rem',
            border: '1px solid grey',
            minHeight: '20em',
            backgroundColor: '#80808012',
          }}>
          <Typography variant="small" color="white">
            Image could not be loaded
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <Box
            id={CANVASCONTAINER}
            className={CANVASCONTAINER}
            sx={{
              height: props.onModal ? 'auto' : `${defaultZoom}vh`,
              width: props.onModal ? 'auto' : 'auto',
              overflow: props.onModal ? 'visible' : 'hidden',
              border: props.onModal ? 'none' : '1px solid grey',
              ...props.sx,
            }}>
            <Tooltip title={DetectionId ?? '-'} followCursor open={open}>
              <canvas
                id={IMAGECANVAS}
                className={IMAGECANVAS}
                style={{
                  width: props.onModal ? '100%' : 'auto',
                  height: props.onModal ? 'auto' : `${zoomValuevh - diffX}vh`,
                  marginBottom: '-7px',
                }}></canvas>
            </Tooltip>
          </Box>
          {props.downloadContext && props.image_url && (
            <>
              <Tooltip title={t('hip_app.visualizer.download_image', 'Download image')}>
                <span style={{ position: 'absolute', bottom: '8px', right: '8px', zIndex: 2 }}>
                  <IconButton
                    id={`${IMAGECANVAS}-download-button`}
                    onClick={event => setDownloadMenuAnchor(event.currentTarget)}
                    disabled={props.isDownloadingImage || props.isLoadingImages || props.isLoadingImageUrl}
                    sx={{
                      color: 'common.white',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      opacity: props.isDownloadingImage ? 1 : 0.6,
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    }}>
                    {props.isDownloadingImage
                      ? <CircularProgress size={22} color="inherit" />
                      : <IconComponent iconName="download-outline" size={22} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={() => setDownloadMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <MenuItem onClick={() => handleDownload(IMAGE_DOWNLOAD_TYPES.RAW)}>
                  {t('hip_app.visualizer.download_raw_image', 'Image only')}
                </MenuItem>
                <MenuItem onClick={() => handleDownload(IMAGE_DOWNLOAD_TYPES.WITH_OBJECTS)}>
                  {t('hip_app.visualizer.download_image_with_objects', 'Image with objects')}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      )}
      {(props.isLoadingImages || props.isLoadingImageUrl) && (
        <LinearProgress
          sx={{
            width: '99.5%',
            backgroundColor: theme.palette.background.default,
          }}
        />
      )}
    </>
  );
};
export default Canvas;
